import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { classnames, Button, Icon, PlainButton } from "@culturehq/components";

import { useClickTransport } from "app/context";
import { useDocumentEvent, useDoor } from "app/hooks";
import { ProfileLink } from "app/links";
import { canCreateEvent } from "app/permissions";
import { containedBy, t, Trans } from "app/utils";

import QuickOptions from "components/display/QuickOptions";
import Flag from "components/common/Flag";
import IncrementUserPoints from "components/admin/IncrementUserPoints";
import UserEditModal from "components/admin/users/UserEditModal";

import getUserName from "app/utils/getUserName";
import styles from "./styles.module.scss";

const MyProfileLinks = ({ profile, viewProfile }) => {
  const { accountInfoEnabled, passwordsEnabled } = profile.organization;

  return (
    <ul className="dropdown-menu">
      {viewProfile && (
        <li>
          <Link to="/profile">
            <Icon icon="person" />{" "}
            <Trans>View My Profile</Trans>
          </Link>
        </li>
      )}
      <li>
        <Link to="/profile/settings">
          <Icon icon="edit" />
          <Trans>Edit My Profile</Trans>
        </Link>
      </li>
      <li>
        <Link to="/profile/settings/emails">
          <Icon icon="ios-bell" />
          <Trans>Edit My Email Notifications</Trans>
        </Link>
      </li>
      {passwordsEnabled && (
        <li>
          <Link to="/profile/settings/password">
            <Icon icon="locked" />
            <Trans>Edit My Password</Trans>
          </Link>
        </li>
      )}
      {accountInfoEnabled && (
        <li>
          <Link to="/profile/status">
            <Icon icon="ios-pulse-strong" />{" "}
            <Trans>See Account Info</Trans>
          </Link>
        </li>
      )}
    </ul>
  );
};

const OtherProfileLinks = ({ onEditOpen, onUsersClicked, profile, user, viewProfile }) => {
  const name = user.name && user.name.replace(/ .*/, "");

  const onEventCreate = () => onUsersClicked([user], null, null);

  return (
    <ul className="dropdown-menu">
      {viewProfile && (
        <li>
          <ProfileLink user={user}>
            <Icon icon="person" />
            {" "}
            <Trans>View {name}&#39;s profile</Trans>
          </ProfileLink>
        </li>
      )}
      {canCreateEvent(profile) && profile.organization.eventsEnabled && (
        <li>
          <Link to="/events/new/type" onClick={onEventCreate}>
            <Icon icon="link" className="rotate-45d" />
            <Trans>Create an event with {name}</Trans>
          </Link>
        </li>
      )}
      {profile.organization.recognitionsEnabled && (
        <li>
          <Link to={{ pathname: "/recognitions", state: { user } }}>
            <Icon icon="megaphone" />
            <Trans> Recognize {getUserName(user)}</Trans>
          </Link>
        </li>
      )}
      {!viewProfile && (
        <li>
          <Flag action="flagAvatar" options={{ userId: user.id }}>
            {onTrigger => (
              <PlainButton onClick={onTrigger}>
                <Icon icon="ios-flag" />
                <Trans>Flag profile as inappropriate</Trans>
              </PlainButton>
            )}
          </Flag>
        </li>
      )}
      {profile.organizationAdmin && (
        <>
          <li>
            <IncrementUserPoints user={user} />
          </li>
          <li>
            <PlainButton onClick={onEditOpen}>
              <Icon icon="edit" />
              <Trans>Edit {name}&#39;s profile</Trans>
            </PlainButton>
          </li>
        </>
      )}
    </ul>
  );
};

const QuickLinksButton = ({ children, noText }) => {
  const { open, onClose, onToggle } = useDoor(false);
  const containerRef = useRef();

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "chq-mdl") && containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    },
    [onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    styles.quick,
    open && styles["quick-open"]
  );

  return (
    <div ref={containerRef} className={classList}>
      {noText
        ? (
          <Button aria-label={t("Open menu")} icon="more" inverted onClick={onToggle} />
        ) : (
          <Button aria-label={t("Open menu")} icon="kabob" inverted onClick={onToggle}>
            <Trans>More</Trans>
          </Button>
        )}
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const QuickLinksKabob = ({ children, theme }) => (
  <QuickOptions theme={theme}>
    {children}
  </QuickOptions>
);

const QuickLinksBox = ({ children, noText, theme, viewProfile }) => {
  if (viewProfile) {
    return <QuickLinksKabob theme={theme}>{children}</QuickLinksKabob>;
  }
  return <QuickLinksButton noText={noText}>{children}</QuickLinksButton>;
};

const QuickLinks = ({ onChange = () => {}, noText = false, profile, theme, user, viewProfile }) => {
  const { onUsersClicked } = useClickTransport();
  const [editOpen, setEditOpen] = useState(false);

  const onEditChange = changed => {
    setEditOpen(false);
    onChange(changed);
  };

  const onEditOpen = () => setEditOpen(true);
  const onEditClose = () => setEditOpen(false);

  return (
    <>
      <QuickLinksBox noText={noText} theme={theme} viewProfile={viewProfile}>
        {user.id === profile.id && (
          <MyProfileLinks profile={profile} viewProfile={viewProfile} />
        )}
        {user.id !== profile.id && (
          <OtherProfileLinks
            onEditOpen={onEditOpen}
            onUsersClicked={onUsersClicked}
            profile={profile}
            user={user}
            viewProfile={viewProfile}
          />
        )}
      </QuickLinksBox>
      {editOpen && (
        <UserEditModal
          user={user}
          onChange={onEditChange}
          onClose={onEditClose}
        />
      )}
    </>
  );
};

export default QuickLinks;
